import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import type { HardwareSupplierPrice, HardwarePricePoint } from '../../utils/materialsDatabaseService';
import {
  lastHardwarePriceChangePoints,
  normalizeHardwarePriceHistory,
  hardwarePercentChange,
  hardwarePriceHistoryPercentChanges,
} from '../../utils/hardwareSupplierPriceHistory';
import { formatPriceHistoryDate } from '../../utils/materialsPriceHistoryFormat';
import './TimberSupplierPriceTrend.css';

function formatUsd(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatPct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function Sparkline({ points }: { points: Array<{ unitPrice: number }> }) {
  const w = 56;
  const h = 22;
  const pad = 2;
  if (points.length < 2) {
    return (
      <svg className="timber-price-sparkline timber-price-sparkline--empty" width={w} height={h} aria-hidden>
        <line x1={pad} y1={h / 2} x2={w - pad} y2={h / 2} stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
      </svg>
    );
  }
  const values = points.map((p) => p.unitPrice);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const coords = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / span) * (h - pad * 2);
    return `${x},${y}`;
  });
  const up = values[values.length - 1]! >= values[0]!;
  return (
    <svg
      className={`timber-price-sparkline ${up ? 'timber-price-sparkline--up' : 'timber-price-sparkline--down'}`}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={coords.join(' ')}
      />
    </svg>
  );
}

export interface HardwareSupplierPriceTrendProps {
  supplierEntry?: HardwareSupplierPrice | null;
  supplierLabel?: string;
  skuLabel?: string;
}

/** Same sparkline + hover history UX as timber, keyed on unitPrice. */
const HardwareSupplierPriceTrend: React.FC<HardwareSupplierPriceTrendProps> = ({
  supplierEntry,
  supplierLabel,
  skuLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);

  const history = useMemo(
    () => normalizeHardwarePriceHistory(supplierEntry?.priceHistory) as HardwarePricePoint[],
    [supplierEntry?.priceHistory],
  );
  const sparkPoints = useMemo(() => lastHardwarePriceChangePoints(history, 3), [history]);
  const changes = useMemo(() => hardwarePriceHistoryPercentChanges(history), [history]);
  const chartData = useMemo(
    () =>
      history.map((p, i) => ({
        i,
        label: formatPriceHistoryDate(p.recordedAt, history.map((h) => h.recordedAt)),
        unitPrice: p.unitPrice,
        ts: p.recordedAt.getTime(),
      })),
    [history],
  );

  const hasTrend = history.length >= 2;

  const showPopover = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setAnchor({
      top: rect.bottom + 8,
      left: Math.min(rect.left, window.innerWidth - 360),
    });
    setOpen(true);
  };

  return (
    <div
      className="timber-price-trend"
      onMouseEnter={(e) => showPopover(e.currentTarget)}
      onMouseLeave={() => setOpen(false)}
      onFocus={(e) => showPopover(e.currentTarget)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      title={hasTrend ? 'Hover for full price history' : 'No price history yet'}
      data-testid="hardware-price-trend"
    >
      {hasTrend ? <Sparkline points={sparkPoints} /> : <span className="timber-price-trend-empty">—</span>}
      {open && anchor && hasTrend && createPortal(
        <div
          className="timber-price-trend-popover"
          style={{ top: anchor.top, left: Math.max(8, anchor.left) }}
          role="tooltip"
        >
          <div className="timber-price-trend-popover-title">
            {supplierLabel || supplierEntry?.supplier || 'Supplier'}
            {skuLabel ? <span className="timber-price-trend-popover-sku">{skuLabel}</span> : null}
          </div>
          <div className="timber-price-trend-chart">
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={48}
                  tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
                />
                <Tooltip
                  formatter={(value) => formatUsd(Number(value ?? 0))}
                  labelFormatter={(label) => String(label)}
                />
                <Line
                  type="monotone"
                  dataKey="unitPrice"
                  name="Unit price"
                  stroke="#1d4ed8"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <table className="timber-price-trend-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Price</th>
                <th>Δ</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p, i) => {
                const prev = i > 0 ? history[i - 1] : null;
                const pct = prev ? hardwarePercentChange(prev.unitPrice, p.unitPrice) : null;
                const cls =
                  pct == null ? '' : pct > 0 ? 'pct-up' : pct < 0 ? 'pct-down' : '';
                return (
                  <tr key={`${p.recordedAt.toISOString()}-${i}`}>
                    <td>{formatPriceHistoryDate(p.recordedAt, history.map((h) => h.recordedAt))}</td>
                    <td>{formatUsd(p.unitPrice)}</td>
                    <td className={cls}>{i === 0 ? '—' : formatPct(pct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {changes.length > 0 && (
            <div className="timber-price-trend-footer">
              {changes.length} change{changes.length === 1 ? '' : 's'} recorded
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default HardwareSupplierPriceTrend;
